<template>
    <MainContent title="Ventas">
        <template #header>
            <template v-if="loading">
                <!-- Skeleton: header card + botones -->
                <Card customClasses="tw:mb-6 tw:transition-none">
                    <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                        <template #skeleton>
                            <div class="tw:flex tw:flex-col tw:md:flex-row tw:items-center tw:justify-between tw:gap-4">
                                <SkeletonItem height="tw:h-8" customClass="tw:w-56 tw:rounded-lg" />
                                <div class="tw:flex tw:flex-wrap tw:gap-3">
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-28 tw:rounded-lg" />
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-36 tw:rounded-lg" />
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-44 tw:rounded-lg" />
                                </div>
                            </div>
                        </template>
                    </Skeleton>
                </Card>

                <!-- Skeleton: 3 stats cards -->
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-6 tw:mb-6">
                    <Card custom-classes="tw:px-4 tw:py-4">
                        <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                            <template #skeleton>
                                <div class="tw:flex tw:flex-col tw:justify-between tw:min-h-[180px]">
                                    <div class="tw:space-y-2">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-32 tw:rounded" />
                                        <SkeletonItem height="tw:h-16" customClass="tw:w-14 tw:rounded-lg" />
                                    </div>
                                    <div class="tw:pt-4 tw:border-t tw:border-gray-100">
                                        <div class="tw:flex tw:justify-between">
                                            <SkeletonItem height="tw:h-4" customClass="tw:w-28 tw:rounded" />
                                            <SkeletonItem height="tw:h-10" customClass="tw:w-20 tw:rounded-lg" />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Skeleton>
                    </Card>
                    <Card custom-classes="tw:px-4 tw:py-4">
                        <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                            <template #skeleton>
                                <div class="tw:space-y-3">
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-42 tw:rounded" />
                                    <div class="tw:flex tw:items-center tw:gap-4 tw:flex-col">
                                        <SkeletonItem height="tw:h-36" customClass="tw:w-36 tw:rounded-full tw:shrink-0" />
                                        <div class="tw:flex tw:flex-col tw:gap-2">
                                            <SkeletonItem height="tw:h-3" customClass="tw:w-12 tw:rounded" />
                                            <div v-for="n in 2" :key="'leg-sk-' + n" class="tw:flex tw:items-center tw:gap-2">
                                                <SkeletonItem height="tw:h-3" customClass="tw:w-3 tw:rounded-full" />
                                                <SkeletonItem height="tw:h-3" customClass="tw:w-20 tw:rounded" />
                                                <SkeletonItem height="tw:h-3" customClass="tw:w-8 tw:rounded" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Skeleton>
                    </Card>
                    <Card custom-classes="tw:px-4 tw:py-4">
                        <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                            <template #skeleton>
                                <div class="tw:space-y-3">
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-24 tw:rounded" />
                                    <SkeletonItem height="tw:h-36" customClass="tw:w-full tw:rounded-lg" />
                                </div>
                            </template>
                        </Skeleton>
                    </Card>
                </div>
            </template>

            <template v-else>
                <!-- Action Header: Panel de Ventas + botones -->
                <Card customClasses="tw:mb-6">
                    <div class="tw:flex tw:flex-col tw:md:flex-row tw:items-center tw:justify-between tw:gap-4">
                        <h1 class="tw:text-2xl tw:font-bold tw:text-gray-900">Panel de Ventas</h1>
                        <div class="tw:flex tw:flex-wrap tw:gap-3">
                            <Button
                                icon="fas fa-tags"
                                as="router-link"
                                :to="{ name: 'back.bases.informe.categorias', params: { lista_id: props.lista_id } }"
                            >
                                Categorías
                            </Button>
                            <Button
                                icon="fas fa-clock"
                                as="router-link"
                                :to="{ name: 'back.bases.informe.plazos-impagados', params: { lista_id: props.lista_id } }"
                            >
                                Estado de Plazos
                            </Button>
                            <Button
                                icon="fas fa-download"
                                @click="modal.show('descargar-informe')"
                            >
                                Descargar Informe
                            </Button>
                        </div>
                    </div>
                </Card>

                <!-- Stats Overview: 3 redesigned cards -->
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-6 tw:mb-6">
                    <!-- Card 1: Total Deportistas -->
                    <Card customClasses=" tw:items-center tw:justify-between">
                        <div class="tw:flex tw:flex-col">
                            <p class="tw:text-sm tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-3">Total Deportistas</p>
                            <h2 class="tw:text-6xl tw:font-bold tw:text-[#b7102a] tw:text-center">{{ totales.totalBases }}</h2>
                        </div>
                        <div class="tw:flex tw:flex-col tw:gap-2 tw:items-center tw:justify-center tw:pt-4 tw:border-t tw:border-gray-100 tw:text-center">
                            <span class="tw:text-sm tw:text-gray-500">Recaudación Total</span>
                            <span class="tw:text-xl tw:font-bold tw:text-gray-900">{{ formatPrice(totales.totalRecaudacion) }}</span>
                        </div>
                    </Card>

                    <!-- Card 2: Renovaciones -->
                    <Card customClasses="tw:items-center tw:justify-between">
                        <p class="tw:text-sm tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-1">Renovaciones/Nuevas altas</p>
                        <div class="tw:flex tw:items-center tw:gap-2 tw:flex-col">
                            <div class="tw:w-40 tw:shrink-0">
                                <Chart
                                    type="PieChart"
                                    :data="renovacionesData"
                                    :options="{ pieHole: 0.4, title: '', legend: { position: 'none' }, colors: ['#3b82f6', '#10b981'] }"
                                    :height="160"
                                    :loading="loading"
                                />
                            </div>
                            <div class="tw:flex tw:flex-col tw:gap-2">
                                <span class="tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider">Estado</span>
                                <div v-for="item in renovacionesLegend" :key="item.label" class="tw:flex tw:items-center tw:gap-2">
                                    <span
                                        class="tw:h-3 tw:w-3 tw:rounded-full tw:shrink-0"
                                        :style="{ backgroundColor: item.label === 'Renovaciones' ? '#3b82f6' : '#10b981' }"
                                    ></span>
                                    <span class="tw:text-sm tw:text-gray-700">{{ item.label }}</span>
                                    <span class="tw:text-sm tw:font-semibold tw:text-gray-900">{{ item.value }}</span>
                                </div>
                            </div>
                        </div>
                    </Card>

                    <!-- Card 3: Venta Diaria -->
                    <Card>
                        <p class="tw:text-sm tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-4">Venta Diaria</p>
                        <Chart
                            type="LineChart"
                            :data="valoresVentaDiariaGrafica"
                            :options="{ title: '', curveType: 'function', legend: { position: 'bottom' } }"
                            :height="180"
                            :loading="loading"
                        />
                    </Card>
                </div>
            </template>
        </template>

        <!-- Tabs skeleton: Card + pills + contenido tipo ventas -->
        <template v-if="loading">
            <Card :padding="0">
                <div class="tw:border-b tw:border-gray-100">
                    <nav class="tw:flex tw:gap-3 tw:-mb-px tw:overflow-x-auto tw:pt-3 tw:ml-5">
                        <SkeletonItem v-for="n in 5" :key="'tab-sk-' + n" height="tw:h-6" customClass="tw:w-28" />
                    </nav>
                </div>
                <div class="tw:relative tw:bg-transparent">
                    <div class="tw:flex tw:justify-between tw:items-end tw:px-6 tw:py-4 tw:border-b tw:border-gray-100">
                        <div class="tw:flex tw:flex-col tw:gap-1">
                            <div class="tw:h-4 tw:w-24 tw:bg-gray-200 tw:rounded tw:my-1"></div>
                            <div class="tw:h-3 tw:w-44 tw:bg-gray-200 tw:rounded tw:my-1"></div>
                        </div>
                    </div>
                    <div class="tw:w-full tw:overflow-x-auto">
                        <table class="tw:w-full tw:text-sm tw:border-collapse">
                            <thead class="tw:text-gray-400 tw:text-xs tw:uppercase tw:tracking-wider">
                                <tr class="tw:border-b tw:border-gray-100">
                                    <th class="tw:px-6 tw:py-3 tw:text-left tw:font-semibold">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-28" />
                                    </th>
                                    <th class="tw:px-6 tw:py-3 tw:text-left tw:font-semibold">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-16" />
                                    </th>
                                    <th class="tw:px-6 tw:py-3 tw:text-left tw:font-semibold">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-14" />
                                    </th>
                                    <th class="tw:px-6 tw:py-3 tw:text-left tw:font-semibold">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-24" />
                                    </th>
                                    <th class="tw:px-6 tw:py-3 tw:text-left tw:font-semibold">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-28" />
                                    </th>
                                    <th class="tw:px-6 tw:py-3 tw:text-left tw:font-semibold">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-12" />
                                    </th>
                                    <th class="tw:px-6 tw:py-3 tw:text-left tw:font-semibold">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-36" />
                                    </th>
                                    <th class="tw:px-6 tw:py-3 tw:text-left tw:font-semibold">
                                        <SkeletonItem height="tw:h-3" customClass="tw:w-14" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="tw:divide-y tw:divide-gray-100">
                                <tr v-for="n in 6" :key="'row-sk-' + n" class="">
                                    <td class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-32" />
                                    </td>
                                    <td class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-16" />
                                    </td>
                                    <td class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-14" />
                                    </td>
                                    <td class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-24" />
                                    </td>
                                    <td class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-28" />
                                    </td>
                                    <td class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-5 tw:rounded-full tw:mx-auto" />
                                    </td>
                                    <td class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-36" />
                                    </td>
                                    <td class="tw:px-6 tw:py-4">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-14" />
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr class="tw:bg-gray-50">
                                    <td colspan="7" class="tw:px-6 tw:py-3">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-10" />
                                    </td>
                                    <td class="tw:px-6 tw:py-3">
                                        <SkeletonItem height="tw:h-4" customClass="tw:w-14" />
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </Card>
        </template>

        <TabsPanel v-else :tabs="tabs">
            <template #tab-ventas>
                <InformeVentasTab
                    :informe="informe"
                    :detalle="detalle"
                />
            </template>
            <template #tab-devoluciones>
                <InformeDevolucionesTab
                    :devoluciones="devoluciones"
                    :devoluciones-detalle="devolucionesDetalle"
                />
            </template>
            <template #tab-plazos>
                <InformePlazosTab :lista-id="props.lista_id" />
            </template>
            <template #tab-calendario>
                <InformeCalendarioTab :lista-id="props.lista_id" />
            </template>
            <template #tab-categorias>
                <InformeCategoriasTab
                    :desglose="desglose"
                />
            </template>
        </TabsPanel>

        <DescargarInformeModal
            name="descargar-informe"
            ruta="back.bases.informe"
            :detalle="true"
            :lista_id="props.lista_id"
            @close="closeModal"
        />
    </MainContent>
</template>

<script setup>
    import { computed, onMounted } from 'vue';
    import { useModal } from '@/composables/common/useModal';

    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import Chart from '@/modules/admin/ui/Chart.vue';
    import TabsPanel from '@/modules/admin/ui/TabsPanel.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    import InformeVentasTab from '@/modules/admin/bases/informe/components/InformeVentasTab.vue';
    import InformeDevolucionesTab from '@/modules/admin/bases/informe/components/InformeDevolucionesTab.vue';
    import InformePlazosTab from '@/modules/admin/bases/informe/components/InformePlazosTab.vue';
    import InformeCalendarioTab from '@/modules/admin/bases/informe/components/InformeCalendarioTab.vue';
    import InformeCategoriasTab from '@/modules/admin/bases/informe/components/InformeCategoriasTab.vue';

    import DescargarInformeModal from '@/modules/admin/bases/informe/components/DescargarInformeModal.vue';

    import { useInformeBases } from '@/composables/admin/bases/informe/useInformeBases';
    import filters from '@/filters';

    const props = defineProps({
        lista_id: { type: [Number, String], required: true }
    });

    const modal = useModal();

    const {
        loading,
        informe,
        detalle,
        totales,
        devoluciones,
        devolucionesDetalle,
        valoresVentaDiariaGrafica,
        renovacionesData,
        valoresRenovaciones,
        desglose,
        fetch
    } = useInformeBases();

    const tabs = computed(() => [
        { key: 'ventas', label: 'Ventas' },
        { key: 'devoluciones', label: 'Devoluciones' },
        { key: 'plazos', label: 'Resumen de plazos' },
        { key: 'calendario', label: 'Calendario de cobros' },
        { key: 'categorias', label: 'Desglose por categoría' }
    ]);

    const renovacionesLegend = computed(() => {
        const items = [];
        if (!valoresRenovaciones.value) return items;
        if (Array.isArray(valoresRenovaciones.value)) {
            valoresRenovaciones.value.forEach(item => {
                items.push({
                    label: item.label || item.estado,
                    value: Number(item.value || item.cantidad || 0)
                });
            });
        } else {
            const data = valoresRenovaciones.value;
            if (data.renovados !== undefined) items.push({ label: 'Renovaciones', value: Number(data.renovados) });
            if (data.nuevos !== undefined) items.push({ label: 'Nuevas Altas', value: Number(data.nuevos) });
        }
        return items;
    });

    function closeModal(name) {
        modal.hide(name);
    }

    const formatPrice = filters.formatear_precio;

    onMounted(() => {
        fetch(null, null, props.lista_id);
    });
</script>
